import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { CATEGORY_COLORS } from "./constants";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ─── §7a.3 status / category / priority semantics ──────────────────────────

/** Tailwind classes for each --status-* token pair */
export const STATUS_TOKEN_CLASS = {
  approved: "bg-status-approved-bg text-status-approved-fg",
  submitted: "bg-status-submitted-bg text-status-submitted-fg",
  rejected: "bg-status-rejected-bg text-status-rejected-fg",
  draft: "bg-status-draft-bg text-status-draft-fg",
  review: "bg-status-review-bg text-status-review-fg",
  void: "bg-status-void-bg text-status-void-fg",
  running: "bg-brand-running text-brand-ink",
};

/** Map domain status strings → §7a.1 status token keys */
const STATUS_TOKEN_BY_VALUE = {
  approved: "approved",
  completed: "approved",
  resolved: "approved",
  active: "approved",
  paid: "approved",
  submitted: "submitted",
  open: "submitted",
  in_progress: "submitted",
  sent: "submitted",
  rejected: "rejected",
  overdue: "rejected",
  draft: "draft",
  review: "review",
  pending: "review",
  on_hold: "review",
  planning: "review",
  partial: "review",
  void: "void",
  cancelled: "void",
  closed: "void",
  inactive: "void",
  archived: "void",
  running: "running",
};

export function statusTokenKey(status) {
  if (!status) return "void";
  return STATUS_TOKEN_BY_VALUE[String(status).toLowerCase()] ?? "void";
}

/** @deprecated Prefer StatusBadge; kept for class-only call sites */
export const STATUS_BADGE = Object.fromEntries(
  Object.keys(STATUS_TOKEN_BY_VALUE).map((k) => [
    k,
    STATUS_TOKEN_CLASS[STATUS_TOKEN_BY_VALUE[k]],
  ])
);

export const APPROVAL_BADGE = {
  pending: STATUS_TOKEN_CLASS.review,
  approved: STATUS_TOKEN_CLASS.approved,
  rejected: STATUS_TOKEN_CLASS.rejected,
};

export const INVOICE_BADGE = {
  draft: STATUS_TOKEN_CLASS.draft,
  sent: STATUS_TOKEN_CLASS.submitted,
  paid: STATUS_TOKEN_CLASS.approved,
  overdue: STATUS_TOKEN_CLASS.rejected,
  void: STATUS_TOKEN_CLASS.void,
  partial: STATUS_TOKEN_CLASS.review,
  cancelled: STATUS_TOKEN_CLASS.void,
};

/** Category is never a filled badge — wrapper only (dot rendered by CategoryBadge) */
export const CATEGORY_BADGE = Object.fromEntries(
  Object.keys(CATEGORY_COLORS).map((k) => [k, "text-brand-ink"])
);

export function categorySeriesVar(category) {
  return CATEGORY_COLORS[category] ?? "var(--brand-steel)";
}

/** Priority is text-only (§7a.3) */
export const PRIORITY_BADGE = {
  low: "font-normal text-brand-steel",
  medium: "font-normal text-brand-steel",
  high: "font-medium text-brand-steel",
  urgent: "font-medium text-danger",
};

export const PRIORITY_TEXT_CLASS = PRIORITY_BADGE;

export const ROLE_BADGE = {
  admin: "bg-brand-tint text-brand-ink",
  manager: "bg-brand-tint text-brand-ink",
  employee: "bg-brand-subtle text-brand-steel",
};

const CHIP_BASE = "inline-flex h-6 items-center gap-1 rounded-md px-2 text-xs font-medium capitalize";

export function statusBadgeClass(status) {
  const key = statusTokenKey(status);
  return cn(CHIP_BASE, STATUS_TOKEN_CLASS[key] ?? STATUS_TOKEN_CLASS.void);
}

export function categoryBadgeClass(_category) {
  return cn("inline-flex h-6 items-center gap-1.5 text-xs font-medium capitalize text-brand-ink");
}

export function priorityBadgeClass(priority) {
  return cn(
    "inline-flex h-6 items-center gap-1 text-xs capitalize",
    PRIORITY_BADGE[priority] ?? "font-normal text-brand-steel"
  );
}

export function approvalBadgeClass(status) {
  return cn(CHIP_BASE, APPROVAL_BADGE[status] ?? STATUS_TOKEN_CLASS.void);
}

export function invoiceBadgeClass(status) {
  return cn(CHIP_BASE, INVOICE_BADGE[status] ?? STATUS_TOKEN_CLASS.void);
}

/**
 * Format seconds into H:MM:SS string.
 * @param {number} totalSeconds
 * @returns {string} e.g. "1:07:03"
 */
export function formatElapsed(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}
